import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, CheckCircle, Lightbulb, Users, Plus, ChevronUp, ChevronDown, Upload, X, Video } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILES = 3;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  featureRequestText: z.string().trim().min(10, "Please provide more detail about your feedback").max(5000, "Feedback must be less than 5000 characters"),
  videoUrl: z.string().url("Please enter a valid URL").max(500, "URL must be less than 500 characters").optional().or(z.literal("")),
});

interface SimilarRequest {
  id: string;
  title: string;
  summary: string;
  similarity: number;
}

interface FeatureRequest {
  id: string;
  title: string | null;
  summary: string | null;
  vote_score: number | null;
  status: string | null;
  created_at: string;
}

type SortOption = "most-upvoted" | "newest" | "most-discussed";

const getUserIdentifier = (): string => {
  let id = localStorage.getItem("bwcx_user_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("bwcx_user_id", id);
  }
  return id;
};

const FeatureRequests = () => {
  const location = useLocation();
  const canonicalUrl = `https://buildwithchrist.ai${location.pathname}`;
  const { organization, isLoading: orgLoading } = useOrganization();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [wantsUpdates, setWantsUpdates] = useState(false);
  const [featureRequestText, setFeatureRequestText] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [similarRequests, setSimilarRequests] = useState<SimilarRequest[]>([]);
  const [wasGrouped, setWasGrouped] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [showForm, setShowForm] = useState(false);
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>("most-upvoted");
  const [userVotes, setUserVotes] = useState<Record<string, number>>({});
  const [votingId, setVotingId] = useState<string | null>(null);

  useEffect(() => {
    if (!orgLoading && organization?.id) {
      fetchRequests();
      fetchUserVotes();
    }
  }, [orgLoading, organization?.id]);

  const fetchRequests = async () => {
    if (!organization?.id) return;
    
    try {
      const { data, error } = await supabase
        .from("feature_requests_normalized")
        .select("id, title, summary, vote_score, status, created_at")
        .eq("organization_id", organization.id)
        .neq("status", "rejected");

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const fetchUserVotes = async () => {
    const userId = getUserIdentifier();
    try {
      const { data, error } = await supabase
        .from("feature_request_votes")
        .select("request_id, vote_value")
        .eq("user_identifier", userId);

      if (error) throw error;
      
      const votes: Record<string, number> = {};
      data?.forEach((v) => {
        votes[v.request_id] = v.vote_value;
      });
      setUserVotes(votes);
    } catch (error) {
      console.error("Error fetching user votes:", error);
    }
  };

  const handleVote = async (requestId: string, voteValue: number) => {
    const userId = getUserIdentifier();
    const currentVote = userVotes[requestId] || 0;
    
    // Toggle vote off if clicking same button
    const newVoteValue = currentVote === voteValue ? 0 : voteValue;
    
    setVotingId(requestId);
    
    try {
      const { error } = await supabase.rpc("update_feature_request_vote", {
        p_request_id: requestId,
        p_user_identifier: userId,
        p_vote_value: newVoteValue,
      });

      if (error) throw error;

      // Update local state
      setUserVotes((prev) => ({ ...prev, [requestId]: newVoteValue }));
      
      // Update vote score in requests list
      setRequests((prev) =>
        prev.map((req) => {
          if (req.id === requestId) {
            const scoreDiff = newVoteValue - currentVote;
            return { ...req, vote_score: (req.vote_score || 0) + scoreDiff };
          }
          return req;
        })
      );
    } catch (error) {
      console.error("Vote error:", error);
      toast.error("Failed to record vote");
    } finally {
      setVotingId(null);
    }
  };

  const sortedRequests = [...requests].sort((a, b) => {
    switch (sortBy) {
      case "most-upvoted":
        return (b.vote_score || 0) - (a.vote_score || 0);
      case "newest":
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case "most-discussed":
        // Using absolute vote count as proxy for discussion
        return Math.abs(b.vote_score || 0) - Math.abs(a.vote_score || 0);
      default:
        return 0;
    }
  });

  const getStatusBadgeVariant = (status: string | null) => {
    switch (status) {
      case "approved":
        return "default";
      case "pending-review":
        return "secondary";
      case "in-progress":
        return "outline";
      default:
        return "secondary";
    }
  };

  const getStatusLabel = (status: string | null) => {
    switch (status) {
      case "approved":
        return "Approved";
      case "pending-review":
        return "Under Review";
      case "in-progress":
        return "In Progress";
      default:
        return status || "Unknown";
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Filter valid files
    const validFiles = files.filter((file) => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`${file.name} is not a supported image format`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds the 5MB size limit`);
        return false;
      }
      return true;
    });

    // Add to existing attachments, respecting max limit
    const remaining = MAX_FILES - attachments.length;
    const filesToAdd = validFiles.slice(0, remaining);
    
    if (validFiles.length > remaining) {
      toast.warning(`Only ${remaining} more image(s) can be added`);
    }
    
    setAttachments((prev) => [...prev, ...filesToAdd]);
    e.target.value = ""; // Reset input
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = formSchema.safeParse({ name, email, featureRequestText, videoUrl });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);

    try {
      // Generate unique ID for this submission's files
      const submissionId = crypto.randomUUID();
      
      // Upload images first
      const uploadedPaths: string[] = [];
      for (const file of attachments) {
        const filePath = `${submissionId}/${file.name}`;
        const { error } = await supabase.storage
          .from("feature-request-attachments")
          .upload(filePath, file);
        if (error) throw new Error(`Failed to upload ${file.name}: ${error.message}`);
        uploadedPaths.push(filePath);
      }

      const { data: rawRequest, error: rawError } = await supabase
        .from("feature_requests_raw")
        .insert({
          name,
          email,
          wants_updates: wantsUpdates,
          feature_request_text: featureRequestText,
          video_url: videoUrl || null,
          attachments: uploadedPaths,
        })
        .select("id")
        .single();

      if (rawError) throw rawError;

      toast.success("Feature request submitted!");

      const { data: normalizeData, error: normalizeError } = await supabase.functions.invoke(
        "feature-request-normalizer",
        {
          body: {
            raw_request_id: rawRequest.id,
            feature_request_text: featureRequestText,
          },
        }
      );

      if (normalizeError) {
        console.error("Normalize error:", normalizeError);
      }

      const normalizedId = normalizeData?.record?.id;

      if (normalizedId) {
        const { error: embeddingError } = await supabase.functions.invoke(
          "generate-embedding",
          {
            body: { requestId: normalizedId },
          }
        );

        if (embeddingError) {
          console.error("Embedding error:", embeddingError);
        }

        const { data: similarData, error: similarError } = await supabase.rpc(
          "find_similar_requests",
          {
            p_request_id: normalizedId,
            p_threshold: 0.8,
          }
        );

        let groupId: string;

        if (similarError) {
          console.error("Similar requests error:", similarError);
          groupId = crypto.randomUUID();
        } else if (similarData && similarData.length > 0) {
          setSimilarRequests(similarData);
          setWasGrouped(true);
          
          const strongestMatch = similarData[0];
          const { data: matchData } = await supabase
            .from("feature_requests_normalized")
            .select("group_id")
            .eq("id", strongestMatch.id)
            .maybeSingle();
          
          groupId = matchData?.group_id || strongestMatch.id;
        } else {
          groupId = crypto.randomUUID();
        }

        await supabase
          .from("feature_requests_normalized")
          .update({ group_id: groupId })
          .eq("id", normalizedId);
      }

      setSubmitted(true);
      fetchRequests();
    } catch (error: any) {
      console.error("Submission error:", error);
      toast.error(error.message || "Failed to submit feature request");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Card className="max-w-xl w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
          <p className="text-muted-foreground mb-6">
            Your feedback has been submitted successfully. We'll review it and may reach out if we need more details.
          </p>

          {wasGrouped && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-sm">Similar requests exist</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your request will be grouped with related ideas to help us prioritize effectively.
                  </p>
                </div>
              </div>
            </div>
          )}

          {similarRequests.length > 0 && (
            <div className="text-left mt-6 pt-6 border-t border-border">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary" />
                Similar Requests
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                We found some similar feature requests that others have submitted:
              </p>
              <ul className="space-y-3">
                {similarRequests.map((req) => (
                  <li key={req.id} className="bg-muted/50 rounded-lg p-3">
                    <p className="font-medium text-sm">{req.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{req.summary}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button
            onClick={() => {
              setSubmitted(false);
              setShowForm(false);
              setName("");
              setEmail("");
              setWantsUpdates(false);
              setFeatureRequestText("");
              setVideoUrl("");
              setAttachments([]);
              setSimilarRequests([]);
              setWasGrouped(false);
            }}
            variant="outline"
            className="mt-6"
          >
            Back to Feedback
          </Button>
        </Card>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Button
          variant="ghost"
          onClick={() => setShowForm(false)}
          className="mb-4"
        >
          ← Back to all feedback
        </Button>
        
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Submit Feedback</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Share your ideas, report bugs, or request new features. Screenshots and video links help us understand your feedback better.
          </p>
        </div>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                disabled={loading}
                className={errors.name ? "border-destructive" : ""}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={loading}
                className={errors.email ? "border-destructive" : ""}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="featureRequestText">Your Feedback</Label>
              <Textarea
                id="featureRequestText"
                value={featureRequestText}
                onChange={(e) => setFeatureRequestText(e.target.value)}
                placeholder="Describe your idea, bug, or feature request. What problem would it solve? How would you use it?"
                rows={6}
                disabled={loading}
                className={errors.featureRequestText ? "border-destructive" : ""}
              />
              {errors.featureRequestText && (
                <p className="text-sm text-destructive">{errors.featureRequestText}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {featureRequestText.length}/5000 characters
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="videoUrl" className="flex items-center gap-2">
                <Video className="w-4 h-4" />
                Video Link (optional)
              </Label>
              <Input
                id="videoUrl"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://www.loom.com/share/... or YouTube URL"
                disabled={loading}
                className={errors.videoUrl ? "border-destructive" : ""}
              />
              {errors.videoUrl && (
                <p className="text-sm text-destructive">{errors.videoUrl}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Paste a Loom, YouTube, or other video URL to help explain your feedback
              </p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Screenshots (optional)
              </Label>
              <div className="border border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="screenshot-upload"
                  disabled={loading || attachments.length >= MAX_FILES}
                />
                <label 
                  htmlFor="screenshot-upload" 
                  className={`cursor-pointer ${attachments.length >= MAX_FILES ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {attachments.length >= MAX_FILES 
                      ? "Maximum images reached" 
                      : "Click to upload screenshots"}
                  </p>
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Max {MAX_FILES} images, 5MB each. JPG, PNG, GIF, WebP
              </p>
              
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {attachments.map((file, i) => (
                    <div key={i} className="relative group">
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`Screenshot ${i + 1}`}
                        className="w-20 h-20 object-cover rounded-md border border-border" 
                      />
                      <button
                        type="button"
                        onClick={() => removeAttachment(i)}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={loading}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="wantsUpdates"
                checked={wantsUpdates}
                onCheckedChange={(checked) => setWantsUpdates(checked === true)}
                disabled={loading}
              />
              <Label htmlFor="wantsUpdates" className="text-sm font-normal cursor-pointer">
                Keep me updated on this submission
              </Label>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {attachments.length > 0 ? "Uploading..." : "Submitting..."}
                </>
              ) : (
                "Submit Feedback"
              )}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <Helmet>
        <title>Feedback & Feature Requests | Build With Christ</title>
        <meta name="description" content="Report bugs, request features, or share feedback for BuildWithChrist.ai. Vote on ideas from the community." />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Feedback & Feature Requests</h1>
          <p className="text-muted-foreground">
            Report bugs, request features, or share ideas — vote on what matters most
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="shrink-0">
          <Plus className="w-4 h-4 mr-2" />
          Submit Feedback
        </Button>
      </div>

      <Tabs value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)} className="mb-6">
        <TabsList>
          <TabsTrigger value="most-upvoted">Most Upvoted</TabsTrigger>
          <TabsTrigger value="newest">Newest</TabsTrigger>
          <TabsTrigger value="most-discussed">Most Discussed</TabsTrigger>
        </TabsList>
      </Tabs>

      {loadingRequests ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : sortedRequests.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">No feedback yet. Be the first to submit!</p>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Submit Feedback
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedRequests.map((request) => {
            const userVote = userVotes[request.id] || 0;
            const isVoting = votingId === request.id;
            
            return (
              <Card key={request.id} className="p-4">
                <div className="flex gap-4">
                  {/* Vote buttons */}
                  <div className="flex flex-col items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 w-8 p-0 ${userVote === 1 ? "text-primary bg-primary/10" : ""}`}
                      onClick={() => handleVote(request.id, 1)}
                      disabled={isVoting}
                    >
                      <ChevronUp className="w-5 h-5" />
                    </Button>
                    <span className={`text-sm font-semibold ${(request.vote_score || 0) > 0 ? "text-primary" : (request.vote_score || 0) < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                      {request.vote_score || 0}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 w-8 p-0 ${userVote === -1 ? "text-destructive bg-destructive/10" : ""}`}
                      onClick={() => handleVote(request.id, -1)}
                      disabled={isVoting}
                    >
                      <ChevronDown className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-lg leading-tight">
                        {request.title || "Untitled Request"}
                      </h3>
                      <Badge variant={getStatusBadgeVariant(request.status)} className="shrink-0">
                        {getStatusLabel(request.status)}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {request.summary || "No summary available"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeatureRequests;
